import { AriaTelemetryClient } from './AriaTelemetryClient';
import { LogBuffer } from './LogBuffer';
import { LogLevel } from './Telemetry.constants';
import { ITelemetryContext, UserBIEvent } from './Telemetry.types';
import { ITelemetryClient } from './telemetryClient.types';
import { IScenarioEvent } from './TelemetryScenarioTracker.types';

export const telemetryClients: ITelemetryClient[] = [];
export let isInitialized: boolean = false;

export const initializeTelemetryClient = (ariaIngestionKey?: string | null) => {
  LogBuffer.getInstance();
  if (ariaIngestionKey) {
    isInitialized = true;

    telemetryClients.push(new AriaTelemetryClient(ariaIngestionKey));
    // Push logs buffered before initialization to telemetry clients.
    LogBuffer.pushLogsToTelemetryClient();
  }
};

/**
 * Logs UI action or event.
 */
export const logUIEvent = <T>(
  event: UserBIEvent<T & Partial<ITelemetryContext>>
) => {
  telemetryClients.forEach(telemetryClient =>
    telemetryClient.logUIEvent(event)
  );
};

/**
 * Logs a scenario for business telemetry indicators.
 * @param event - properties for the scenario e.g. step, elapsed, delta etc.
 */
export const logScenario = <T>(
  event: IScenarioEvent<T & Partial<ITelemetryContext>>
) => {
  telemetryClients.forEach(telemetryClient =>
    telemetryClient.logScenario(event)
  );
};

/**
 * Logs that a page, or a container is displayed to the user
 */
export const logPageView = (
  pageName: string,
  pageCategory?: string,
  uri?: string,
  refUri?: string,
  properties?: { [key: string]: string }
) => {
  const logPageViewCallBack = () =>
    telemetryClients.forEach(telemetryClient =>
      telemetryClient.logPageView(
        pageName,
        pageCategory,
        uri,
        refUri,
        properties
      )
    );

  isInitialized
    ? logPageViewCallBack()
    : LogBuffer.addToLogBuffer(logPageViewCallBack);
};

/**
 * Logs an exception that has been caught
 */
export const logException = (
  exceptionName: string,
  error: Error,
  properties?: { [key: string]: string }
) => {
  const logExceptionCallback = () =>
    telemetryClients.forEach(telemetryClient =>
      telemetryClient.logException(exceptionName, error, properties)
    );

  isInitialized
    ? logExceptionCallback()
    : LogBuffer.addToLogBuffer(logExceptionCallback);
};

/**
 * Logs a diagnostic trace message such as entering or leaving a function
 */
export const logTrace = (
  message: string,
  severityLevel: LogLevel = LogLevel.Information,
  properties?: { [key: string]: string }
) => {
  const logTraceCallback = () =>
    telemetryClients.forEach(telemetryClient =>
      telemetryClient.logTrace(message, severityLevel, properties)
    );

  isInitialized
    ? logTraceCallback()
    : LogBuffer.addToLogBuffer(logTraceCallback);
};

/**
 * Manually triggers an immediate send of all telemetry information that is still in the buffer
 */
export const flushTelemetry = (callback?: () => void) => {
  telemetryClients.forEach(telemetryClient =>
    // tslint:disable-next-line:no-empty ~ Disable since flush callback is unused.
    telemetryClient.flush(callback ?? (() => {}))
  );
};
